import { Plus } from 'lucide-react';
import { Player, Team } from 'modules/players/domain/interfaces/player';
import { useCallback, useMemo, useState } from 'react';
import { AsyncState } from 'shared/utils/async-state';
import { theme } from 'styles/theme/theme';
import { UDEntityList } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import useModalAddToTeam from '../hooks/use-modal-add-to-team';
import PlayerTeamRow from './player-team-row';

interface IProps {
  player: Player;
  teams: Team[];
}

function TeamsList(props: IProps) {
  const { teams, player } = props;
  const { open: openModalAddToTeam, Modal: ModalAddToTeam } = useModalAddToTeam();

  const [searchValue, setSearchValue] = useState('');

  const filteredTeams = useMemo(() => {
    if (!searchValue) {
      return teams;
    }

    return teams.filter((team) => team.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [searchValue, teams]);

  // Вкладка получает готовый массив и сети не видит: ветки загрузки и ошибки рисует
  // карточка игрока выше по дереву (`player-view-page.tsx`). Считать надо по
  // отфильтрованному списку, иначе ветка «не найдено» никогда не покажется.
  const state: AsyncState = useMemo(
    () => ({ status: filteredTeams.length > 0 ? 'ready' : 'empty', isRefreshing: false }),
    [filteredTeams.length],
  );

  const handleAddToTeam = useCallback(() => openModalAddToTeam(player), [openModalAddToTeam, player]);

  const addButton = useMemo(
    () => (
      <UDButton className="flex items-center" variant="dark" onClick={handleAddToTeam}>
        <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
        <span>Добавить в команду</span>
      </UDButton>
    ),
    [handleAddToTeam],
  );

  const renderTeam = useCallback(
    (team: Team, index: number) => <PlayerTeamRow player={player} team={team} index={index + 1} />,
    [player],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <UDEntityList<Team>
        items={filteredTeams}
        renderItem={renderTeam}
        getItemKey={(team) => team.id}
        state={state}
        search={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Поиск по команде"
        // Кнопка либо в тулбаре, либо под текстом пустого состояния, но не в обоих
        // местах сразу: на пустом списке она дублировала бы себя.
        actions={teams.length > 0 ? addButton : undefined}
        emptyText="Игрок не состоит ни в одной команде"
        emptySubtitle="Давайте добавим?"
        emptyAction={addButton}
        notFoundText="Команды не найдены"
      />

      <ModalAddToTeam />
    </div>
  );
}

export default TeamsList;
